/**
 * Safely parse user roles into an array of integer role IDs.
 * Handles: "4,8", [4, 8], ["4", "8"], 4, null, undefined.
 *
 * @param {Object|string|number|Array} userOrRoles
 * @returns {number[]}
 */
export const getUserRoles = (userOrRoles) => {
  if (!userOrRoles) return [];

  let raw = userOrRoles;
  if (typeof userOrRoles === 'object' && !Array.isArray(userOrRoles)) {
    raw = userOrRoles.user_role_id ?? userOrRoles.role_id ?? userOrRoles.roles ?? '';
  }

  if (Array.isArray(raw)) {
    return Array.from(new Set(raw.map(Number).filter(n => !isNaN(n) && n > 0)));
  }

  return Array.from(
    new Set(
      String(raw)
        .split(',')
        .map(s => Number(s.trim()))
        .filter(n => !isNaN(n) && n > 0)
    )
  );
};

/**
 * Check if the user has a specific role ID.
 *
 * @param {Object|string|number|Array} user
 * @param {number} roleId
 * @returns {boolean}
 */
export const hasRole = (user, roleId) => {
  return getUserRoles(user).includes(Number(roleId));
};

/**
 * Check if the user has ANY of the specified role IDs.
 *
 * @param {Object|string|number|Array} user
 * @param {number[]} allowedRoleIds
 * @returns {boolean}
 */
export const hasAnyRole = (user, allowedRoleIds = []) => {
  const userRoles = getUserRoles(user);
  const targets = allowedRoleIds.map(Number);
  return targets.some(target => userRoles.includes(target));
};

/**
 * Check if the user has ALL of the specified role IDs.
 *
 * @param {Object|string|number|Array} user
 * @param {number[]} requiredRoleIds
 * @returns {boolean}
 */
export const hasAllRoles = (user, requiredRoleIds = []) => {
  const userRoles = getUserRoles(user);
  const targets = requiredRoleIds.map(Number);
  return targets.every(target => userRoles.includes(target));
};

/**
 * Format an array of roles to a comma-separated string for DB/API payload.
 *
 * @param {number[]|string[]} roles
 * @returns {string}
 */
export const formatRolesForPayload = (roles) => {
  return getUserRoles(roles).join(',');
};
